/**
 * ONE TAP — in-game HUD.  Iconography, not prose (DESIGN.md §9).
 *
 * Design rules this file is built around:
 *
 *  - **Every binary state is encoded twice: in VALUE and in SHAPE.** Hue is only
 *    ever the third channel. Chambered is a big near-white slug on a near-black
 *    chip; empty is a small hollow dark outline on white. Convert the screen to
 *    greyscale and the two are still unmistakable — that is the acceptance test,
 *    and hue-only signalling is a defect, not a style choice.
 *  - **Nothing lives in the weapon's screen quadrant.** The viewmodel owns the
 *    bottom-right. Every persistent readout is in the bottom-LEFT column
 *    (`.ot-left`) or the top strip, so no HUD element can ever occlude the gun.
 *  - The crosshair answers "can I shoot right now?" without being read, and it
 *    carries a guaranteed contrast floor: a near-black outer stroke wrapping a
 *    pure-white core. On a sunlit wall the black reads; on a shadowed ceiling
 *    slab the white reads. There is no surface in the arena where both vanish.
 *  - Health is a *state*, not a bar, and HEALTHY is not drawn at all — the HUD
 *    only speaks up when the news is bad.  WOUNDED gets a whole-screen treatment.
 *  - The incoming-pass ring teaches the catch: it converges as the gun arrives,
 *    and it shouts LOOK AT IT when the gun is outside your catch cone, because
 *    that is the rule new players trip over.
 *  - **Every verb must announce itself.** A playtester could not find the grapple
 *    at all — the anchors were in range the whole time and nothing on screen ever
 *    said so. So the crosshair grows four brackets the moment an anchor is
 *    targetable and closes them when the tether bites, and they are the SAME
 *    shape as the reticle the renderer draws on the anchor out in the world. Two
 *    marks, one gesture, no copy (DESIGN.md §8).
 *
 * Performance contract: every per-frame write is a `transform`, an `opacity`, a
 * CSS custom property, or a `textContent` that actually changed. Each HUD region
 * is absolutely positioned inside a `contain: layout style paint` box, so nothing
 * here can reflow the page mid-match.
 */

import {
  GRAPPLE_NONE,
  CATCH_RADIUS,
  CATCH_VIEW_CONE,
  DASH_CHARGES,
  DASH_CHARGES_UNARMED,
  GRAPPLE_AIM_CONE,
  GRAPPLE_COOLDOWN_MS,
  GRAPPLE_RANGE,
  PLAYER_CROUCH_EYE_HEIGHT,
  PLAYER_EYE_HEIGHT,
  PULSE_COOLDOWN_MS,
  ROUNDS_TO_WIN,
  ROUND_TIME_MS,
  SUDDEN_DEATH_MS,
  TEAM_A,
  TEAM_B,
  TEAM_NAMES,
} from '@shared/constants';
import { clamp01 } from '@shared/math';
import { ARENA } from '@shared/sim/arena';
import type { GameEvent, GunEntity, PlayerState, WorldState } from '@shared/types';
import { GunState, HealthState, HitZone, RoundPhase } from '@shared/types';
import { esc, getSettings, teamColor } from './settings';

/** `ParentNode` is ambiguous here — @cloudflare/workers-types declares a global one
 *  for HTMLRewriter — so this takes the structural shape it actually needs. */
type Queryable = { querySelector(selectors: string): Element | null };

const q = <T extends HTMLElement>(root: Queryable, sel: string): T => {
  const found = root.querySelector(sel);
  if (!found) throw new Error(`hud: missing element ${sel}`);
  return found as T;
};

/** How far ahead we look for an incoming gun, in seconds. */
const PASS_LOOKAHEAD_S = 1.5;
/** Closest-approach distance that counts as "this is coming at me". */
const PASS_NEAR_RADIUS = CATCH_RADIUS * 2.4;
/** Time-to-arrival at which the catch cue snaps to its "now" state. */
const PASS_WINDOW_S = 0.2;

/**
 * Cosine of the grapple aim cone, precomputed. The crosshair asks "is an anchor
 * targetable right now" every single frame, and a dot product beats an `acos` per anchor
 * per frame for a question whose answer is a boolean.
 */
const GRAPPLE_COS = Math.cos(GRAPPLE_AIM_CONE);
const GRAPPLE_RANGE_SQ = GRAPPLE_RANGE * GRAPPLE_RANGE;

const KILLFEED_MS = 5200;
const KILLFEED_MAX = 5;
const TOAST_MS = 1900;
const TOAST_MAX = 3;
const DAMAGE_MS = 1300;
const DAMAGE_MAX = 4;

/**
 * Ability glyphs. Three genuinely different silhouettes — a double chevron, a
 * ring between two brackets, an anchor — so the row is read by shape at a
 * glance instead of by three identical dots changing colour.
 *
 * Each path is drawn twice: a thick near-black understroke and a thin white
 * overstroke. That is the same two-tone trick the crosshair uses, and it is why
 * the glyph survives both the pale tile (cooling) and the slate tile (ready).
 */
const ABILITY_GLYPHS = {
  dash: 'M6 5.5L12.5 12L6 18.5M13 5.5L19.5 12L13 18.5',
  pulse:
    'M12 9.5a2.5 2.5 0 1 1 0 5a2.5 2.5 0 1 1 0-5M6.8 7.6a6.6 6.6 0 0 0 0 8.8M17.2 7.6a6.6 6.6 0 0 1 0 8.8',
  grapple: 'M12 7V19.2M12 7a2 2 0 1 0 0-4a2 2 0 1 0 0 4M8.6 9.6h6.8M4.8 13.4a7.2 7.2 0 0 0 14.4 0',
} as const;

/**
 * Spectator glyphs. Two genuinely different silhouettes, because the two states mean
 * two different things: an EYE means "you are riding somebody", a RING means "there is
 * nobody left to ride and the camera is circling the arena". Shape carries it; the bar
 * never changes hue to say which mode it is in.
 */
const SPECTATE_GLYPHS = {
  eye: 'M2.6 12S6.2 6.4 12 6.4S21.4 12 21.4 12S17.8 17.6 12 17.6S2.6 12 2.6 12M12 9.4a2.6 2.6 0 1 1 0 5.2a2.6 2.6 0 1 1 0-5.2',
  ring: 'M12 4.4a7.6 7.6 0 1 1 0 15.2a7.6 7.6 0 1 1 0-15.2M12 1.8v4.2',
} as const;

const specGlyph = (name: keyof typeof SPECTATE_GLYPHS): string => `
  <svg class="ot-spec__svg" data-g="${name}" viewBox="0 0 24 24" aria-hidden="true" focusable="false">
    <path class="ot-glyph__under" d="${SPECTATE_GLYPHS[name]}"></path>
    <path class="ot-glyph__over" d="${SPECTATE_GLYPHS[name]}"></path>
  </svg>`;

const ability = (id: string, key: string, glyph: keyof typeof ABILITY_GLYPHS): string => `
  <div class="ot-ability" id="${id}" style="--cd:0">
    <span class="ot-ability__tile">
      <svg class="ot-ability__glyph" viewBox="0 0 24 24" aria-hidden="true" focusable="false">
        <path class="ot-glyph__under" d="${ABILITY_GLYPHS[glyph]}"></path>
        <path class="ot-glyph__over" d="${ABILITY_GLYPHS[glyph]}"></path>
      </svg>
    </span>
    <span class="ot-ability__key">${key}</span>
  </div>`;

/**
 * A teammate row: a bearing arrow, a NAME, and two glyphs.
 *
 * The health hexagon is drawn as three overlapping paths and the CSS shows exactly the
 * ones the state calls for, so each state is a genuinely different SILHOUETTE rather than
 * the same pill in a different colour:
 *
 *   ok       solid filled hexagon
 *   wounded  hollow hexagon with its lower half filled  (a shape at half)
 *   down     hollow hexagon with a cross struck through it
 *
 * The chamber slug repeats the player's own bottom-left readout at small scale — filled
 * near-white for LOADED, a hollow dark outline for EMPTY, an empty dashed holder for
 * UNARMED. One shape language for "is there a round in it", used in both places.
 */
const MATE_HTML = `
  <span class="ot-mate__arrow" aria-hidden="true"></span>
  <span class="ot-mate__name"></span>
  <span class="ot-mate__marks" aria-hidden="true">
    <svg class="ot-mate__hp" viewBox="0 0 20 20" focusable="false">
      <path class="ot-hp__shell" d="M10 2.2L17 6.1V13.9L10 17.8L3 13.9V6.1Z"></path>
      <path class="ot-hp__half" d="M10 17.8L3 13.9V10H17V13.9Z"></path>
      <path class="ot-hp__out" d="M5.9 5.9L14.1 14.1M14.1 5.9L5.9 14.1"></path>
    </svg>
    <span class="ot-mate__slug" data-arms="none"></span>
  </span>`;

const HUD_HTML = `
<div class="ot-hud__vignette" aria-hidden="true"></div>

<!-- THE ONLY PERSISTENT FULL-FRAME LAYER IN THE HUD, and it is an edge rim.
     There is deliberately no DOM layer for the WOUNDED state: that rim is drawn once,
     by the renderer (see THE WOUND RIM in gfx/vfx.ts), to a measured area budget. A
     second one here would double it, which is exactly how the old treatment ended up
     washing red across the middle of the frame on the one beat a wounded player most
     needs to read the room. -->
<div class="ot-hud__rim" aria-hidden="true"></div>

<div class="ot-top">
  <div class="ot-score" id="ot-score">
    <div class="ot-score__side ot-score__side--a">
      <span class="ot-score__team" id="ot-score-name-a"></span>
      <span class="ot-score__num" id="ot-score-a">0</span>
      <div class="ot-score__pips" id="ot-pips-a" aria-hidden="true"></div>
    </div>
    <div class="ot-score__mid">
      <div class="ot-timer" id="ot-timer">
        <span class="ot-timer__num" id="ot-timer-num">1:30</span>
        <span class="ot-timer__tag" id="ot-timer-tag">ROUND 1</span>
      </div>
    </div>
    <div class="ot-score__side ot-score__side--b">
      <span class="ot-score__team" id="ot-score-name-b"></span>
      <span class="ot-score__num" id="ot-score-b">0</span>
      <div class="ot-score__pips" id="ot-pips-b" aria-hidden="true"></div>
    </div>
  </div>
</div>

<ul class="ot-killfeed" id="ot-killfeed" aria-live="polite" aria-label="Kill feed"></ul>

<div class="ot-center">
  <!-- The four corner brackets are the grapple's affordance at the aim point. They are
       the SAME shape as the reticle drawn on the anchor itself out in the world, so the
       two read as one mechanism the first time a player sees them converge. -->
  <div class="ot-crosshair" id="ot-crosshair" data-state="unarmed" data-grapple="none">
    <span class="ot-cross__dot"></span>
    <span class="ot-cross__tick ot-cross__tick--u"></span>
    <span class="ot-cross__tick ot-cross__tick--d"></span>
    <span class="ot-cross__tick ot-cross__tick--l"></span>
    <span class="ot-cross__tick ot-cross__tick--r"></span>
    <span class="ot-cross__ring"></span>
    <span class="ot-cross__lock" aria-hidden="true">
      <i class="ot-cross__cnr ot-cross__cnr--tl"></i>
      <i class="ot-cross__cnr ot-cross__cnr--tr"></i>
      <i class="ot-cross__cnr ot-cross__cnr--bl"></i>
      <i class="ot-cross__cnr ot-cross__cnr--br"></i>
    </span>
  </div>

  <div class="ot-hitmarks" id="ot-hitmarks" aria-hidden="true"></div>
  <div class="ot-damage" id="ot-damage" aria-hidden="true"></div>

  <!-- One label, no second line. A hint under the ring put a third block of text
       within 100px of the crosshair, and the label plus the ring's colour already
       say everything the player can act on. DESIGN.md §8. -->
  <div class="ot-pass" id="ot-pass" hidden>
    <span class="ot-pass__ring"></span>
    <span class="ot-pass__lead"></span>
    <span class="ot-pass__arrow" id="ot-pass-arrow"></span>
    <span class="ot-pass__label" id="ot-pass-label">INCOMING</span>
  </div>
</div>

<div class="ot-toasts" id="ot-toasts" aria-live="polite"></div>

<!-- Health is only ever drawn when it is bad news. A permanent HEALTHY label is
     the state the player is in for most of every round, so it carries no
     information. WOUNDED and ELIMINATED get the dark chip, under the clock,
     where nothing can occlude them and they never crowd the crosshair. -->
<div class="ot-health" id="ot-health" data-state="healthy">
  <span class="ot-health__word" id="ot-health-word">HEALTHY</span>
</div>

<!-- The bottom-LEFT column. The weapon owns the bottom-right quadrant, so every
     persistent readout lives here and physically cannot overlap the viewmodel.
     Read bottom-up: what can I do (abilities), what am I holding (chamber),
     who is with me (mates). -->
<div class="ot-left">
  <div class="ot-mates" id="ot-mates" role="group" aria-label="Teammates"></div>

  <!-- ONE glyph, ONE word. The old readout was a coloured dot plus CHAMBERED
       plus TEAM GUN plus IN YOUR HANDS — four pieces of text answering one
       question, sitting on top of the gun. DESIGN.md §9. When you are holding
       nothing the slug is replaced by a pointer at the team gun, because "where
       is it" is a direction, not a sentence. -->
  <div class="ot-arm" id="ot-arm" data-state="none">
    <span class="ot-arm__slug" aria-hidden="true"></span>
    <span class="ot-arm__mark" id="ot-arm-mark" aria-hidden="true"></span>
    <span class="ot-arm__word" id="ot-arm-word">UNARMED</span>
  </div>

  <div class="ot-abilities" id="ot-abilities" role="group" aria-label="Abilities">
    ${ability('ot-ab-dash', 'SHIFT', 'dash')}
    ${ability('ot-ab-pulse', 'F', 'pulse')}
    ${ability('ot-ab-grapple', 'E', 'grapple')}
  </div>
</div>

<!-- SPECTATOR BAR. Only ever on screen while the player is eliminated, which is the
     one moment the copy budget (DESIGN.md §8) can afford a name and three key caps:
     they are not playing, and being stranded with no idea what they are looking at or
     how to change it is exactly the failure this replaced.

     It sits bottom-CENTRE rather than in the left column because the viewmodel is gone
     while dead, so the bottom-centre band is guaranteed clear — and because the left
     column is still carrying the teammate cards this bar refers to.

     "You are dead" is NOT repeated here: the ELIMINATED chip under the clock already
     says it, and the round state is the clock itself. This bar answers exactly one
     question — whose eyes am I behind, and how do I change them. -->
<div class="ot-spec" id="ot-spec" hidden data-mode="follow" role="status" aria-live="polite">
  <span class="ot-spec__glyph" aria-hidden="true">${specGlyph('eye')}${specGlyph('ring')}</span>
  <span class="ot-spec__name" id="ot-spec-name"></span>
  <span class="ot-spec__dots" id="ot-spec-dots" aria-hidden="true"></span>
  <span class="ot-spec__keys" id="ot-spec-keys" aria-hidden="true">
    <b class="ot-spec__key ot-spec__key--prev" id="ot-spec-prev"></b>
    <b class="ot-spec__key ot-spec__key--next" id="ot-spec-next"></b>
    <b class="ot-spec__key ot-spec__key--pov" id="ot-spec-pov"></b>
  </span>
</div>

<div class="ot-banner" id="ot-banner" hidden>
  <span class="ot-banner__main" id="ot-banner-main"></span>
  <span class="ot-banner__sub" id="ot-banner-sub"></span>
</div>

<div class="ot-stats" id="ot-stats"><span id="ot-stat-ping">— ms</span><span id="ot-stat-fps" hidden>— fps</span></div>
<div class="ot-hud__conn" id="ot-hud-conn" role="status" aria-live="polite" hidden></div>`;

interface Timed {
  el: HTMLElement;
  ttl: number;
}

interface DamageMark extends Timed {
  angle: number;
}

interface MateRefs {
  root: HTMLElement;
  name: HTMLElement;
  slug: HTMLElement;
  arrow: HTMLElement;
  lastName: string;
  lastState: string;
  lastArms: string;
}

/** Whether the grapple key would do anything at this instant. */
type GrappleCue = 'none' | 'ready' | 'locked';

// ---------------------------------------------------------------------------
// Spectator state, published by the game loop
// ---------------------------------------------------------------------------

/**
 * Which camera the dead player is riding. `live` means they are not dead at all.
 *
 *   deathcam  the two-second pull-back that shows what killed them
 *   follow    riding a living teammate, free-look restored
 *   orbit     nobody left alive; the arena orbits itself while the round resolves
 */
export type SpectateMode = 'live' | 'deathcam' | 'follow' | 'orbit';

export interface SpectateView {
  mode: SpectateMode;
  /** Id of the teammate being spectated, so their card can be marked. */
  targetId: string;
  /** Name of the teammate being spectated. Empty outside `follow`. */
  targetName: string;
  /** 1-based position of the target among living teammates; 0 when there is none. */
  index: number;
  /** How many living teammates there are to cycle through. */
  count: number;
  firstPerson: boolean;
  /** Live key labels, resolved from the player's real bindings by the game loop. */
  nextKey: string;
  prevKey: string;
  povKey: string;
}

const LIVE_SPECTATE: SpectateView = {
  mode: 'live',
  targetId: '',
  targetName: '',
  index: 0,
  count: 0,
  firstPerson: false,
  nextKey: '',
  prevKey: '',
  povKey: '',
};

let spectate: SpectateView = LIVE_SPECTATE;

/**
 * The death camera lives in the game loop (it owns the camera, the collision world and
 * the bindings) but its *readout* is a HUD element. The fixed `UI` surface has no slot
 * for it and inventing one would mean editing a module this agent does not own, so the
 * loop publishes here instead and the HUD reads it during its normal per-frame pass.
 *
 * One value, replaced wholesale, read once a frame: no listeners, no allocation per
 * frame, and no way for the two to disagree about which target is on screen.
 */
export function publishSpectate(v: SpectateView): void {
  spectate = v;
}

export function clearSpectate(): void {
  spectate = LIVE_SPECTATE;
}

export class Hud {
  readonly element: HTMLElement;

  private readonly crosshair: HTMLElement;
  private readonly killfeed: HTMLElement;
  private readonly matesRoot: HTMLElement;
  private readonly toasts: HTMLElement;
  private readonly hitmarks: HTMLElement;
  private readonly damageRoot: HTMLElement;

  private readonly pass: HTMLElement;
  private readonly passArrow: HTMLElement;
  private readonly passLabel: HTMLElement;

  private readonly arm: HTMLElement;
  private readonly armWord: HTMLElement;
  private readonly armMark: HTMLElement;

  private readonly healthBox: HTMLElement;
  private readonly healthWord: HTMLElement;

  private readonly scoreA: HTMLElement;
  private readonly scoreB: HTMLElement;
  private readonly pipsA: HTMLElement;
  private readonly pipsB: HTMLElement;
  private readonly timerNum: HTMLElement;
  private readonly timerTag: HTMLElement;
  private readonly timerBox: HTMLElement;

  private readonly abDash: HTMLElement;
  private readonly abPulse: HTMLElement;
  private readonly abGrapple: HTMLElement;

  private readonly spec: HTMLElement;
  private readonly specName: HTMLElement;
  private readonly specDots: HTMLElement;
  private readonly specPrev: HTMLElement;
  private readonly specNext: HTMLElement;
  private readonly specPov: HTMLElement;

  private readonly banner: HTMLElement;
  private readonly bannerMain: HTMLElement;
  private readonly bannerSub: HTMLElement;

  private readonly statPing: HTMLElement;
  private readonly statFps: HTMLElement;
  private readonly conn: HTMLElement;

  private readonly mates = new Map<string, MateRefs>();
  private readonly feed: Timed[] = [];
  private readonly toastList: Timed[] = [];
  private readonly hits: Timed[] = [];
  private readonly damage: DamageMark[] = [];

  // Cached last-written values — the HUD only touches the DOM on a real change.
  private lastCross = '';
  private lastArmWord = '';
  private lastArmState = '';
  private lastHealth = '';
  private lastTimer = '';
  private lastTag = '';
  private lastScoreA = -1;
  private lastScoreB = -1;
  private lastDashFill = '';
  private lastGrappleCue: GrappleCue | '' = '';
  private lastSudden = false;
  private lastPhase: RoundPhase | null = null;
  private lastLocalTeam = -1;
  private lastPingText = '';
  private lastFpsText = '';

  private lastSpecMode: SpectateMode | '' = '';
  private lastSpecName = '';
  private lastSpecCount = -1;
  private lastSpecIndex = -1;
  private lastSpecPov = false;
  private lastSpecKeys = '';
  private specTargetId = '';

  private bannerTtl = 0;
  private passVisible = false;

  constructor() {
    const el = document.createElement('section');
    el.className = 'ot-screen ot-hud';
    el.hidden = true;
    el.innerHTML = HUD_HTML;
    this.element = el;

    this.crosshair = q(el, '#ot-crosshair');
    this.killfeed = q(el, '#ot-killfeed');
    this.matesRoot = q(el, '#ot-mates');
    this.toasts = q(el, '#ot-toasts');
    this.hitmarks = q(el, '#ot-hitmarks');
    this.damageRoot = q(el, '#ot-damage');

    this.pass = q(el, '#ot-pass');
    this.passArrow = q(el, '#ot-pass-arrow');
    this.passLabel = q(el, '#ot-pass-label');

    this.arm = q(el, '#ot-arm');
    this.armWord = q(el, '#ot-arm-word');
    this.armMark = q(el, '#ot-arm-mark');

    this.healthBox = q(el, '#ot-health');
    this.healthWord = q(el, '#ot-health-word');

    this.scoreA = q(el, '#ot-score-a');
    this.scoreB = q(el, '#ot-score-b');
    this.pipsA = q(el, '#ot-pips-a');
    this.pipsB = q(el, '#ot-pips-b');
    this.timerNum = q(el, '#ot-timer-num');
    this.timerTag = q(el, '#ot-timer-tag');
    this.timerBox = q(el, '#ot-timer');

    this.abDash = q(el, '#ot-ab-dash');
    this.abPulse = q(el, '#ot-ab-pulse');
    this.abGrapple = q(el, '#ot-ab-grapple');

    this.spec = q(el, '#ot-spec');
    this.specName = q(el, '#ot-spec-name');
    this.specDots = q(el, '#ot-spec-dots');
    this.specPrev = q(el, '#ot-spec-prev');
    this.specNext = q(el, '#ot-spec-next');
    this.specPov = q(el, '#ot-spec-pov');

    this.banner = q(el, '#ot-banner');
    this.bannerMain = q(el, '#ot-banner-main');
    this.bannerSub = q(el, '#ot-banner-sub');

    this.statPing = q(el, '#ot-stat-ping');
    this.statFps = q(el, '#ot-stat-fps');
    this.conn = q(el, '#ot-hud-conn');

    q(el, '#ot-score-name-a').textContent = TEAM_NAMES[TEAM_A];
    q(el, '#ot-score-name-b').textContent = TEAM_NAMES[TEAM_B];
    this.buildPips(this.pipsA);
    this.buildPips(this.pipsB);
    el.style.setProperty('--team-a', teamColor(TEAM_A));
    el.style.setProperty('--team-b', teamColor(TEAM_B));
  }

  show(): void {
    this.element.hidden = false;
  }

  hide(): void {
    this.element.hidden = true;
    this.clearTransients();
    // Leaving the match must not leave a spectator bar latched on for the next one.
    clearSpectate();
    this.spec.hidden = true;
    this.lastSpecMode = '';
    this.lastSpecName = '';
    this.lastSpecCount = -1;
    this.lastSpecIndex = -1;
    this.lastSpecKeys = '';
    this.specTargetId = '';
  }

  setStats(fps: number, pingMs: number): void {
    const ping = `${Math.round(pingMs)} ms`;
    if (ping !== this.lastPingText) {
      this.lastPingText = ping;
      this.statPing.textContent = ping;
      this.statPing.dataset.q = pingMs < 60 ? 'good' : pingMs < 130 ? 'ok' : 'bad';
    }
    const show = getSettings().showFps;
    this.statFps.hidden = !show;
    if (show) {
      const t = `${Math.round(fps)} fps`;
      if (t !== this.lastFpsText) {
        this.lastFpsText = t;
        this.statFps.textContent = t;
        this.statFps.dataset.q = fps >= 90 ? 'good' : fps >= 55 ? 'ok' : 'bad';
      }
    }
  }

  setConnection(state: 'idle' | 'connecting' | 'connected' | 'error' | 'closed', msg?: string): void {
    const label =
      state === 'connecting'
        ? 'RECONNECTING…'
        : state === 'error'
          ? (msg ?? 'CONNECTION PROBLEM')
          : state === 'closed'
            ? (msg ?? 'DISCONNECTED')
            : '';
    this.conn.textContent = label;
    this.conn.dataset.state = state;
    this.conn.hidden = label === '';
  }

  // -------------------------------------------------------------------------
  // Per-frame update
  // -------------------------------------------------------------------------

  update(w: WorldState, localId: string, dtSec: number): void {
    const p = w.players[localId];
    this.tickTransients(dtSec);
    this.updateScoreAndClock(w, p ?? null);
    this.updateSpectate();
    if (!p) return;

    if (p.team !== this.lastLocalTeam) {
      this.lastLocalTeam = p.team;
      this.element.style.setProperty('--mine', teamColor(p.team));
      this.element.style.setProperty('--theirs', teamColor(p.team === TEAM_A ? TEAM_B : TEAM_A));
      this.element.dataset.mine = p.team === TEAM_A ? 'a' : 'b';
    }

    const held = this.gunOf(w, p.heldGun);
    const teamGun = held ?? this.teamGun(w, p);
    const grapple = this.grappleCue(p);

    this.updateCrosshair(p, held, grapple);
    this.updateArm(p, teamGun, held);
    this.updateHealth(p);
    this.updateMates(w, p);
    this.updateAbilities(p, held !== null, grapple);
    this.updatePass(w, p);
  }

  /**
   * Is there an anchor the grapple would take right now?
   *
   * Mirrors `pickAnchor` in @shared/sim/movement — the same cone and the same range —
   * minus the line-of-sight raycast, which needs a collision world the HUD has no
   * business holding. The consequence of that omission is at worst a bracket that
   * appears a fraction of a second early through a pillar; the crosshair never claims a
   * grapple that would have been refused for any other reason.
   *
   * The anchor table comes from `ARENA` because a snapshot carries entities, not
   * geometry. There is one arena in this game and the server ships that same table.
   */
  private grappleCue(p: PlayerState): GrappleCue {
    if (p.healthState === HealthState.Dead) return 'none';
    if (p.grappleAnchorId !== GRAPPLE_NONE) return 'locked';
    if (p.grappleCooldownMs > 0) return 'none';

    const eyeY = p.pos.y + (p.crouching ? PLAYER_CROUCH_EYE_HEIGHT : PLAYER_EYE_HEIGHT);
    const cp = Math.cos(p.pitch);
    const fx = -Math.sin(p.yaw) * cp;
    const fy = Math.sin(p.pitch);
    const fz = -Math.cos(p.yaw) * cp;

    const anchors = ARENA.anchors;
    for (let i = 0; i < anchors.length; i++) {
      const a = anchors[i]!;
      const dx = a.pos.x - p.pos.x;
      const dy = a.pos.y - eyeY;
      const dz = a.pos.z - p.pos.z;
      const d2 = dx * dx + dy * dy + dz * dz;
      if (d2 > GRAPPLE_RANGE_SQ || d2 < 1e-6) continue;
      if (dx * fx + dy * fy + dz * fz >= GRAPPLE_COS * Math.sqrt(d2)) return 'ready';
    }
    return 'none';
  }

  // -------------------------------------------------------------------------
  // Events
  // -------------------------------------------------------------------------

  handle(e: GameEvent, w: WorldState, localId: string): void {
    const nameOf = (id: string): string => w.players[id]?.name ?? 'SOMEONE';
    const teamOf = (id: string): number => w.players[id]?.team ?? TEAM_A;
    const local = w.players[localId];

    switch (e.t) {
      case 'shot':
        if (e.player === localId && e.hitPlayer && e.zone !== HitZone.None) {
          this.hitMarker(e.zone === HitZone.Head ? 'head' : 'body');
        }
        break;

      case 'dryfire':
        if (e.player === localId) {
          this.crosshair.classList.remove('is-dry');
          void this.crosshair.offsetWidth;
          this.crosshair.classList.add('is-dry');
          // One word. "CATCH TO CHAMBER" repeated the lobby's CATCH A PASS TO
          // RELOAD at the one moment the player is already being taught it by the
          // dry click, the crosshair snapping shut and the chamber chip reading
          // EMPTY. DESIGN.md §8: do not say the same thing in two places.
          this.toast('EMPTY', '', 'bad');
        }
        break;

      case 'kill': {
        if (e.killer === localId) this.hitMarker('kill');
        this.feedLine(
          `<span class="ot-feed__who" style="--c:${teamColor(teamOf(e.killer))}">${esc(nameOf(e.killer))}</span>` +
            `<span class="ot-feed__icon ${e.zone === HitZone.Head ? 'is-head' : 'is-body'}" aria-hidden="true"></span>` +
            `<span class="ot-feed__who" style="--c:${teamColor(teamOf(e.victim))}">${esc(nameOf(e.victim))}</span>`,
          e.killer === localId || e.victim === localId ? 'is-mine' : '',
        );
        if (e.victim === localId) {
          this.damageFrom(w, localId, e.killer);
          this.toast('ELIMINATED', `BY ${nameOf(e.killer).toUpperCase()}`, 'bad');
        } else if (e.killer === localId) {
          this.toast(e.zone === HitZone.Head ? 'ONE TAP' : 'DOWN', '', 'good');
        }
        break;
      }

      case 'wound':
        if (e.victim === localId) {
          this.damageFrom(w, localId, e.attacker);
          this.element.classList.remove('is-hurt');
          void this.element.offsetWidth;
          this.element.classList.add('is-hurt');
        } else if (e.attacker === localId) {
          this.hitMarker('body');
        }
        break;

      case 'heal':
        if (e.player === localId) this.toast('HEALED', 'CHAMBER SPENT', 'good');
        break;

      case 'catch': {
        const mine = e.player === localId;
        const friendly = teamOf(e.player) === (local?.team ?? -1);
        if (e.intercept) {
          this.feedLine(
            `<span class="ot-feed__who" style="--c:${teamColor(teamOf(e.player))}">${esc(nameOf(e.player))}</span>` +
              `<span class="ot-feed__tag is-intercept">INTERCEPT</span>`,
            friendly ? 'is-mine' : '',
          );
        }
        if (mine) {
          if (e.intercept) this.toast('INTERCEPT', '', 'hot');
          else if (e.chambered) this.toast('LOADED', e.raw ? 'RAW' : '', 'hot');
          else this.toast('CAUGHT', 'HEALED · EMPTY', 'good');
          this.crosshair.classList.remove('is-rack');
          void this.crosshair.offsetWidth;
          this.crosshair.classList.add('is-rack');
        } else if (friendly && e.chambered) {
          this.feedLine(
            `<span class="ot-feed__who" style="--c:${teamColor(teamOf(e.player))}">${esc(nameOf(e.player))}</span>` +
              `<span class="ot-feed__tag is-catch">LOADED</span>`,
            'is-mine',
          );
        }
        break;
      }

      case 'pickup':
        if (e.player === localId) this.toast('PICKED UP', 'EMPTY', 'warn');
        break;

      case 'disarm':
        this.feedLine(
          `<span class="ot-feed__who" style="--c:${teamColor(teamOf(e.by))}">${esc(nameOf(e.by))}</span>` +
            `<span class="ot-feed__tag is-pulse">PULSED</span>` +
            `<span class="ot-feed__who" style="--c:${teamColor(teamOf(e.player))}">${esc(nameOf(e.player))}</span>`,
          e.by === localId || e.player === localId ? 'is-mine' : '',
        );
        break;

      case 'gunreset':
        if (e.reason === 'void') this.toast('GUN LOST', 'RESPAWNING', 'warn');
        break;

      case 'roundstart':
        this.showBanner(`ROUND ${e.round}`, 'GET READY', 'start', 2200);
        break;

      case 'roundend': {
        if (e.reason === 'draw' || e.winner < 0) {
          this.showBanner('DRAW', 'NOBODY SCORES', 'draw', 3200);
        } else {
          const mine = e.winner === (local?.team ?? -1);
          this.showBanner(
            `${TEAM_NAMES[e.winner === TEAM_A ? TEAM_A : TEAM_B]} TAKES IT`,
            e.reason === 'timeout' ? 'ON THE CLOCK' : 'ELIMINATION',
            mine ? 'win' : 'lose',
            3200,
          );
        }
        break;
      }

      case 'suddendeath':
        this.showBanner('HIGH NOON', 'ALL GUNS VISIBLE', 'sudden', 2600);
        break;

      case 'matchend': {
        const mine = e.winner === (local?.team ?? -1);
        this.showBanner(
          mine ? 'VICTORY' : 'DEFEAT',
          TEAM_NAMES[e.winner === TEAM_A ? TEAM_A : TEAM_B],
          mine ? 'win' : 'lose',
          6000,
        );
        break;
      }

      case 'playerjoin':
        this.feedLine(
          `<span class="ot-feed__who" style="--c:${teamColor(e.team)}">${esc(e.name)}</span>` +
            `<span class="ot-feed__tag">JOINED</span>`,
          '',
        );
        break;

      case 'playerleave':
        this.feedLine(
          `<span class="ot-feed__who">${esc(nameOf(e.player))}</span><span class="ot-feed__tag">LEFT</span>`,
          '',
        );
        break;

      default:
        break;
    }
  }

  dispose(): void {
    this.clearTransients();
    this.element.remove();
  }

  // -------------------------------------------------------------------------
  // Sections
  // -------------------------------------------------------------------------

  private updateCrosshair(p: PlayerState, held: GunEntity | null, grapple: GrappleCue): void {
    const state =
      p.healthState === HealthState.Dead
        ? 'dead'
        : held === null
          ? 'unarmed'
          : held.chambered
            ? 'loaded'
            : 'empty';
    if (state !== this.lastCross) {
      this.lastCross = state;
      this.crosshair.dataset.state = state;
    }
    // The grapple's affordance at the aim point: four brackets that converge when an
    // anchor is acquired and close right down once the tether bites. Deliberately
    // colourless — traversal has no colour in this game, and the chamber owns the amber.
    if (grapple !== this.lastGrappleCue) {
      this.lastGrappleCue = grapple;
      this.crosshair.dataset.grapple = grapple;
    }
  }

  /**
   * The chamber readout: one glyph, one word, in the bottom-left column.
   *
   * The two states a player must never confuse are separated on THREE channels
   * at once, in descending order of how fast they are read:
   *
   *   value  LOADED sits on a near-black chip with a near-white slug;
   *          EMPTY  sits on a white chip with a near-black hollow outline.
   *   shape  LOADED is a solid filled bar; EMPTY is an open outline.
   *   scale  EMPTY is visibly smaller — it is the lesser state, and it says so.
   *
   * Hue (the amber) is applied last, as a tint on the slug, and nothing depends
   * on it. Desaturate the frame and the readout still works.
   *
   * With nothing in hand there is no slug: a pointer aims at the team gun
   * instead, filled if that gun is chambered and hollow if it is not. A
   * direction is not a sentence, so no prose replaces it.
   */
  private updateArm(p: PlayerState, teamGun: GunEntity | null, held: GunEntity | null): void {
    let word: string;
    let state: string;

    if (p.healthState === HealthState.Dead) {
      // The chip is hidden outright while eliminated (`.ot-hud.is-dead .ot-arm`) —
      // the spectator bar owns that moment and saying SPECTATING in two places is
      // exactly what DESIGN.md §8 forbids. This is the fallback if that rule ever
      // goes away, not the live path.
      word = 'SPECTATING';
      state = 'dead';
    } else if (held) {
      word = held.chambered ? 'LOADED' : 'EMPTY';
      state = held.chambered ? 'loaded' : 'empty';
    } else {
      word = 'UNARMED';
      state = 'none';
    }

    if (word !== this.lastArmWord) {
      this.lastArmWord = word;
      this.armWord.textContent = word;
      this.arm.classList.remove('is-flip');
      void this.arm.offsetWidth;
      this.arm.classList.add('is-flip');
    }
    if (state !== this.lastArmState) {
      this.lastArmState = state;
      this.arm.dataset.state = state;
    }

    // The pointer only exists in the unarmed state, and only when the gun has a
    // real position to point at.
    const trackable =
      state === 'none' &&
      teamGun !== null &&
      teamGun.state !== GunState.Void &&
      teamGun.holder !== p.id;
    this.armMark.hidden = !trackable;
    if (!trackable || !teamGun) return;

    const dx = teamGun.pos.x - p.pos.x;
    const dz = teamGun.pos.z - p.pos.z;
    const fx = -Math.sin(p.yaw);
    const fz = -Math.cos(p.yaw);
    const rx = Math.cos(p.yaw);
    const rz = -Math.sin(p.yaw);
    const bearing = Math.atan2(dx * rx + dz * rz, dx * fx + dz * fz);
    this.armMark.style.transform = `rotate(${bearing.toFixed(3)}rad)`;
    this.armMark.dataset.chambered = teamGun.chambered ? 'yes' : 'no';
  }

  /**
   * One word, and only when it is not `HEALTHY` — the CSS hides the healthy
   * state outright. There is no second line explaining how to heal: the heal is
   * a discovery (DESIGN.md §8), and a sentence there would have been the fourth
   * thing this game teaches.
   */
  private updateHealth(p: PlayerState): void {
    const dead = p.healthState === HealthState.Dead;
    const wounded = p.healthState === HealthState.Wounded;
    const word = dead ? 'ELIMINATED' : wounded ? 'WOUNDED' : 'HEALTHY';

    if (word !== this.lastHealth) {
      this.lastHealth = word;
      this.healthWord.textContent = word;
      this.healthBox.dataset.state = dead ? 'dead' : wounded ? 'wounded' : 'healthy';
      this.element.classList.toggle('is-wounded', wounded);
      this.element.classList.toggle('is-dead', dead);
    }
  }

  private updateMates(w: WorldState, p: PlayerState): void {
    const fovHalf = (getSettings().fov * 0.5 * Math.PI) / 180;
    const seen = new Set<string>();

    // View basis. Convention matches @shared/math: yaw 0 looks down -Z.
    const fx = -Math.sin(p.yaw);
    const fz = -Math.cos(p.yaw);
    const rx = Math.cos(p.yaw);
    const rz = -Math.sin(p.yaw);

    for (const id in w.players) {
      const m = w.players[id];
      if (!m || id === p.id || m.team !== p.team) continue;
      seen.add(id);

      let refs = this.mates.get(id);
      if (!refs) {
        const root = document.createElement('div');
        root.className = 'ot-mate';
        // The row is a graphic, so it carries its meaning as a label rather than as text
        // on screen. `group` is what makes that label actually reach assistive tech.
        root.setAttribute('role', 'group');
        root.innerHTML = MATE_HTML;
        refs = {
          root,
          name: root.querySelector('.ot-mate__name') as HTMLElement,
          slug: root.querySelector('.ot-mate__slug') as HTMLElement,
          arrow: root.querySelector('.ot-mate__arrow') as HTMLElement,
          lastName: '',
          lastState: '',
          lastArms: '',
        };
        root.style.setProperty('--c', teamColor(m.team));
        this.matesRoot.appendChild(root);
        this.mates.set(id, refs);
        // A card built after the spectate target was chosen still has to wear the mark.
        if (id === this.specTargetId) root.classList.add('is-watched');
      }

      // A name and two glyphs. The card used to be a name plus OK plus UNARMED — three
      // text fields answering two questions that are both shapes (DESIGN.md §9). The
      // hexagon's FILL carries health and the cartridge's FILL carries the chamber, so
      // both survive greyscale, peripheral vision and a colourblind player.
      const nm = m.name.toUpperCase();
      const st =
        m.healthState === HealthState.Dead
          ? 'down'
          : m.healthState === HealthState.Wounded
            ? 'wounded'
            : 'ok';
      const gun = this.gunOf(w, m.heldGun);
      const arms = gun ? (gun.chambered ? 'loaded' : 'empty') : 'none';
      const changed = nm !== refs.lastName || st !== refs.lastState || arms !== refs.lastArms;

      if (nm !== refs.lastName) {
        refs.lastName = nm;
        refs.name.textContent = nm;
      }
      if (st !== refs.lastState) {
        refs.lastState = st;
        refs.root.dataset.state = st;
      }
      if (arms !== refs.lastArms) {
        refs.lastArms = arms;
        refs.slug.dataset.arms = arms;
      }
      // Nothing here is drawn as text, so the row states itself once, for assistive tech
      // only, whenever any of the three facts changes.
      if (changed) refs.root.setAttribute('aria-label', `${nm} · ${st} · ${arms}`);

      // Bearing relative to the camera: 0 = dead ahead, positive = to the right.
      const dx = m.pos.x - p.pos.x;
      const dz = m.pos.z - p.pos.z;
      const fwd = dx * fx + dz * fz;
      const right = dx * rx + dz * rz;
      const bearing = Math.atan2(right, fwd);
      const offscreen = Math.abs(bearing) > fovHalf;
      refs.arrow.style.transform = `rotate(${bearing.toFixed(3)}rad)`;
      refs.root.classList.toggle('is-offscreen', offscreen);
    }

    for (const [id, refs] of this.mates) {
      if (!seen.has(id)) {
        refs.root.remove();
        this.mates.delete(id);
      }
    }
  }

  /**
   * All three abilities speak one language: the tile fills with slate as the
   * ability becomes available and drains as it is spent. Readiness is read from
   * HOW MUCH of the tile is dark, and which ability it is from the silhouette —
   * neither read depends on hue, and neither is a dot.
   *
   * Dash has charges rather than a cooldown, so its fill is charges/max. Two of
   * three charges is a two-thirds tile, which is exactly the truth.
   */
  private updateAbilities(p: PlayerState, armed: boolean, grapple: GrappleCue): void {
    const max = armed ? DASH_CHARGES : DASH_CHARGES_UNARMED;
    const have = Math.max(0, Math.min(max, Math.floor(p.dashCharges)));
    const dash = max > 0 ? have / max : 0;
    const dashText = dash.toFixed(3);
    if (dashText !== this.lastDashFill) {
      this.lastDashFill = dashText;
      this.abDash.style.setProperty('--cd', dashText);
      this.abDash.classList.toggle('is-ready', have > 0);
    }

    const pulse = clamp01(1 - p.pulseCooldownMs / PULSE_COOLDOWN_MS);
    this.abPulse.style.setProperty('--cd', pulse.toFixed(3));
    this.abPulse.classList.toggle('is-ready', pulse >= 1);

    const hook = clamp01(1 - p.grappleCooldownMs / GRAPPLE_COOLDOWN_MS);
    this.abGrapple.style.setProperty('--cd', hook.toFixed(3));
    this.abGrapple.classList.toggle('is-ready', hook >= 1);
    // The third and quietest place the grapple announces itself: the anchor glyph is
    // ringed while a target is acquired, so "E does something RIGHT NOW" is answered even
    // by a player whose eyes never leave the bottom-left column.
    this.abGrapple.classList.toggle('is-target', grapple !== 'none');
  }

  /**
   * Incoming-pass cue. Finds the airborne gun on a collision course with the
   * player, converges a ring on it, and — the part that actually teaches the
   * game — screams when the gun is outside the catch cone, because you cannot
   * catch what you are not looking at.
   */
  private updatePass(w: WorldState, p: PlayerState): void {
    if (p.healthState === HealthState.Dead) {
      this.setPassVisible(false);
      return;
    }

    const eyeY = p.pos.y + (p.crouching ? PLAYER_CROUCH_EYE_HEIGHT : PLAYER_EYE_HEIGHT);
    let best: GunEntity | null = null;
    let bestT = Infinity;
    let bestClosest = Infinity;

    for (const g of w.guns) {
      if (g.state !== GunState.InFlight) continue;
      if (g.lastThrower === p.id && g.throwLockoutMs > 0) continue; // can't self-catch

      const rx = g.pos.x - p.pos.x;
      const ry = g.pos.y - eyeY;
      const rz = g.pos.z - p.pos.z;
      const vx = g.vel.x - p.vel.x;
      const vy = g.vel.y - p.vel.y;
      const vz = g.vel.z - p.vel.z;
      const vv = vx * vx + vy * vy + vz * vz;
      if (vv < 1e-4) continue;

      const t = -(rx * vx + ry * vy + rz * vz) / vv;
      if (t < 0 || t > PASS_LOOKAHEAD_S) continue;

      const cx = rx + vx * t;
      const cy = ry + vy * t;
      const cz = rz + vz * t;
      const closest = Math.sqrt(cx * cx + cy * cy + cz * cz);
      if (closest > PASS_NEAR_RADIUS) continue;

      if (t < bestT) {
        bestT = t;
        bestClosest = closest;
        best = g;
      }
    }

    if (!best) {
      this.setPassVisible(false);
      return;
    }

    // Are we actually looking at it? CATCH_VIEW_CONE is the rule that makes a
    // pass cost both players their aim.
    const dx = best.pos.x - p.pos.x;
    const dy = best.pos.y - eyeY;
    const dz = best.pos.z - p.pos.z;
    const dlen = Math.hypot(dx, dy, dz) || 1;
    const cp = Math.cos(p.pitch);
    const vfx = -Math.sin(p.yaw) * cp;
    const vfy = Math.sin(p.pitch);
    const vfz = -Math.cos(p.yaw) * cp;
    const cosAng = (dx * vfx + dy * vfy + dz * vfz) / dlen;
    const inCone = cosAng >= Math.cos(CATCH_VIEW_CONE);

    const fromMate = best.lastThrower ? w.players[best.lastThrower]?.team === p.team : false;
    const t01 = clamp01(bestT / PASS_LOOKAHEAD_S);
    const imminent = bestT <= PASS_WINDOW_S && bestClosest <= CATCH_RADIUS;

    this.pass.style.setProperty('--t', t01.toFixed(3));
    this.pass.style.setProperty('--c', best.chambered ? 'var(--loaded)' : 'var(--empty)');
    this.pass.classList.toggle('is-window', imminent && inCone);
    this.pass.classList.toggle('is-look', !inCone);
    this.pass.classList.toggle('is-hostile', !fromMate);

    // Bearing to the gun, so the LOOK AT IT arrow points the right way.
    const fx = -Math.sin(p.yaw);
    const fz = -Math.cos(p.yaw);
    const rxb = Math.cos(p.yaw);
    const rzb = -Math.sin(p.yaw);
    const bearing = Math.atan2(dx * rxb + dz * rzb, dx * fx + dz * fz);
    this.passArrow.style.transform = `rotate(${bearing.toFixed(3)}rad)`;

    const label = !inCone ? 'LOOK AT IT' : imminent ? 'CATCH' : fromMate ? 'INCOMING' : 'INTERCEPT';
    if (this.passLabel.textContent !== label) this.passLabel.textContent = label;

    this.setPassVisible(true);
  }

  private setPassVisible(v: boolean): void {
    if (v === this.passVisible) return;
    this.passVisible = v;
    this.pass.hidden = !v;
    if (v) {
      this.pass.classList.remove('is-in');
      void this.pass.offsetWidth;
      this.pass.classList.add('is-in');
    }
  }

  /**
   * The spectator bar. Reads the value the game loop published this frame (see
   * `publishSpectate`) and writes only what actually changed.
   *
   * It is deliberately absent during the death cam: those two seconds are a shot, not
   * a menu, and the player has an ELIMINATED chip and a kill toast on screen already.
   * The bar appears the moment control returns to them, which is also the moment the
   * key caps start meaning something.
   */
  private updateSpectate(): void {
    const s = spectate;
    const on = s.mode === 'follow' || s.mode === 'orbit';

    if (s.targetId !== this.specTargetId) {
      const prev = this.mates.get(this.specTargetId);
      if (prev) prev.root.classList.remove('is-watched');
      this.specTargetId = on ? s.targetId : '';
      const now = this.mates.get(this.specTargetId);
      if (now) now.root.classList.add('is-watched');
    }

    if (s.mode !== this.lastSpecMode) {
      this.lastSpecMode = s.mode;
      this.spec.hidden = !on;
      this.spec.dataset.mode = s.mode;
      if (on) {
        this.spec.classList.remove('is-in');
        void this.spec.offsetWidth;
        this.spec.classList.add('is-in');
      }
    }
    if (!on) return;

    // Orbit has no target, so the name slot carries the arena instead of a player.
    // One word, and it is the only word this bar ever adds to a player's name.
    const name = s.mode === 'orbit' ? 'ARENA' : s.targetName;
    if (name !== this.lastSpecName) {
      this.lastSpecName = name;
      this.specName.textContent = name;
    }

    if (s.count !== this.lastSpecCount) {
      this.lastSpecCount = s.count;
      this.specDots.textContent = '';
      for (let i = 0; i < s.count; i++) {
        const pip = document.createElement('i');
        pip.className = 'ot-spec__pip';
        this.specDots.appendChild(pip);
      }
      this.lastSpecIndex = -1;
    }
    if (s.index !== this.lastSpecIndex) {
      this.lastSpecIndex = s.index;
      const pips = this.specDots.children;
      for (let i = 0; i < pips.length; i++) {
        (pips[i] as HTMLElement).classList.toggle('is-on', i === s.index - 1);
      }
    }

    const keys = `${s.prevKey}|${s.nextKey}|${s.povKey}`;
    if (keys !== this.lastSpecKeys) {
      this.lastSpecKeys = keys;
      this.specPrev.textContent = s.prevKey;
      this.specNext.textContent = s.nextKey;
      this.specPov.textContent = s.povKey;
    }
    // The POV key is meaningless while orbiting, and its glyph carries which view is
    // live: a filled lens is first person, a hollow one is over the shoulder.
    this.specPov.hidden = s.mode !== 'follow';
    if (s.firstPerson !== this.lastSpecPov) {
      this.lastSpecPov = s.firstPerson;
      this.specPov.dataset.pov = s.firstPerson ? 'first' : 'third';
    }
    // Cycling only exists when there is somebody else to cycle to, and a one-of-one
    // counter is a row of pips that says nothing.
    const many = s.count > 1 && s.mode === 'follow';
    this.specDots.hidden = !many;
    this.specPrev.hidden = !many;
    this.specNext.hidden = !many;
  }

  private updateScoreAndClock(w: WorldState, p: PlayerState | null): void {
    const m = w.match;

    if (m.scoreA !== this.lastScoreA) {
      this.lastScoreA = m.scoreA;
      this.scoreA.textContent = String(m.scoreA);
      this.bumpPips(this.pipsA, m.scoreA);
      this.scoreA.classList.remove('is-bump');
      void this.scoreA.offsetWidth;
      this.scoreA.classList.add('is-bump');
    }
    if (m.scoreB !== this.lastScoreB) {
      this.lastScoreB = m.scoreB;
      this.scoreB.textContent = String(m.scoreB);
      this.bumpPips(this.pipsB, m.scoreB);
      this.scoreB.classList.remove('is-bump');
      void this.scoreB.offsetWidth;
      this.scoreB.classList.add('is-bump');
    }

    let msLeft: number;
    let tag: string;
    if (m.phase === RoundPhase.Warmup) {
      msLeft = Math.max(0, m.phaseEndsAtMs - w.timeMs);
      tag = 'GET READY';
    } else if (m.phase === RoundPhase.Live) {
      msLeft = Math.max(0, m.roundEndsAtMs - w.timeMs);
      tag = m.suddenDeath ? 'HIGH NOON' : `ROUND ${m.round}`;
    } else if (m.phase === RoundPhase.RoundEnd) {
      msLeft = 0;
      tag = 'ROUND OVER';
    } else if (m.phase === RoundPhase.MatchEnd) {
      msLeft = 0;
      tag = 'MATCH OVER';
    } else {
      msLeft = ROUND_TIME_MS;
      tag = 'WARMUP';
    }

    const text =
      m.phase === RoundPhase.Warmup
        ? String(Math.ceil(msLeft / 1000))
        : formatClock(msLeft);
    if (text !== this.lastTimer) {
      this.lastTimer = text;
      this.timerNum.textContent = text;
    }
    if (tag !== this.lastTag) {
      this.lastTag = tag;
      this.timerTag.textContent = tag;
    }

    const sudden = m.suddenDeath || (m.phase === RoundPhase.Live && msLeft <= SUDDEN_DEATH_MS);
    if (sudden !== this.lastSudden) {
      this.lastSudden = sudden;
      this.timerBox.classList.toggle('is-sudden', sudden);
      this.element.classList.toggle('is-sudden', sudden);
    }
    this.timerBox.classList.toggle('is-countdown', m.phase === RoundPhase.Warmup);

    if (m.phase !== this.lastPhase) {
      this.lastPhase = m.phase;
      this.element.dataset.phase = String(m.phase);
      if (m.phase === RoundPhase.Live) this.clearTransients(true);
    }
  }

  // -------------------------------------------------------------------------
  // Transients: kill feed, toasts, hit markers, damage arcs, banner
  // -------------------------------------------------------------------------

  private tickTransients(dtSec: number): void {
    const dt = Math.min(dtSec, 0.25);

    for (let i = this.feed.length - 1; i >= 0; i--) {
      const f = this.feed[i]!;
      f.ttl -= dt * 1000;
      if (f.ttl <= 0) {
        f.el.remove();
        this.feed.splice(i, 1);
      } else if (f.ttl < 500) {
        f.el.style.opacity = (f.ttl / 500).toFixed(2);
      }
    }

    for (let i = this.toastList.length - 1; i >= 0; i--) {
      const t = this.toastList[i]!;
      t.ttl -= dt * 1000;
      if (t.ttl <= 0) {
        t.el.remove();
        this.toastList.splice(i, 1);
      }
    }

    for (let i = this.hits.length - 1; i >= 0; i--) {
      const h = this.hits[i]!;
      h.ttl -= dt * 1000;
      if (h.ttl <= 0) {
        h.el.remove();
        this.hits.splice(i, 1);
      }
    }

    for (let i = this.damage.length - 1; i >= 0; i--) {
      const d = this.damage[i]!;
      d.ttl -= dt * 1000;
      if (d.ttl <= 0) {
        d.el.remove();
        this.damage.splice(i, 1);
      } else {
        d.el.style.opacity = clamp01(d.ttl / DAMAGE_MS).toFixed(2);
      }
    }

    if (this.bannerTtl > 0) {
      this.bannerTtl -= dt * 1000;
      if (this.bannerTtl <= 0) {
        this.banner.hidden = true;
        this.banner.className = 'ot-banner';
      }
    }
  }

  private clearTransients(keepFeed = false): void {
    if (!keepFeed) {
      for (const f of this.feed) f.el.remove();
      this.feed.length = 0;
    }
    for (const t of this.toastList) t.el.remove();
    this.toastList.length = 0;
    for (const h of this.hits) h.el.remove();
    this.hits.length = 0;
    for (const d of this.damage) d.el.remove();
    this.damage.length = 0;
  }

  private feedLine(html: string, cls: string): void {
    const li = document.createElement('li');
    li.className = `ot-feed__row ${cls}`;
    li.innerHTML = html;
    this.killfeed.insertBefore(li, this.killfeed.firstChild);
    this.feed.unshift({ el: li, ttl: KILLFEED_MS });
    while (this.feed.length > KILLFEED_MAX) {
      const drop = this.feed.pop();
      drop?.el.remove();
    }
  }

  private toast(main: string, sub: string, kind: 'good' | 'bad' | 'warn' | 'hot'): void {
    const div = document.createElement('div');
    div.className = `ot-toast is-${kind}`;
    div.innerHTML = `<b>${esc(main)}</b>${sub ? `<em>${esc(sub)}</em>` : ''}`;
    this.toasts.insertBefore(div, this.toasts.firstChild);
    this.toastList.unshift({ el: div, ttl: TOAST_MS });
    while (this.toastList.length > TOAST_MAX) {
      const drop = this.toastList.pop();
      drop?.el.remove();
    }
  }

  private hitMarker(kind: 'head' | 'body' | 'kill'): void {
    const span = document.createElement('span');
    span.className = `ot-hitmark is-${kind}`;
    this.hitmarks.appendChild(span);
    this.hits.push({ el: span, ttl: 420 });
  }

  private damageFrom(w: WorldState, localId: string, attackerId: string): void {
    const me = w.players[localId];
    const from = w.players[attackerId];
    if (!me || !from) return;
    const dx = from.pos.x - me.pos.x;
    const dz = from.pos.z - me.pos.z;
    const fx = -Math.sin(me.yaw);
    const fz = -Math.cos(me.yaw);
    const rx = Math.cos(me.yaw);
    const rz = -Math.sin(me.yaw);
    const angle = Math.atan2(dx * rx + dz * rz, dx * fx + dz * fz);

    const span = document.createElement('span');
    span.className = 'ot-dmg';
    span.style.transform = `rotate(${angle.toFixed(3)}rad)`;
    this.damageRoot.appendChild(span);
    this.damage.push({ el: span, ttl: DAMAGE_MS, angle });
    while (this.damage.length > DAMAGE_MAX) {
      const drop = this.damage.shift();
      drop?.el.remove();
    }
  }

  private showBanner(main: string, sub: string, kind: string, ms: number): void {
    this.bannerMain.textContent = main;
    this.bannerSub.textContent = sub;
    this.banner.className = `ot-banner is-${kind}`;
    this.banner.hidden = false;
    void this.banner.offsetWidth;
    this.banner.classList.add('is-in');
    this.bannerTtl = ms;
  }

  // -------------------------------------------------------------------------
  // Helpers
  // -------------------------------------------------------------------------

  /** `PlayerState.heldGun` is an index into `WorldState.guns`, or -1. */
  private gunOf(w: WorldState, index: number): GunEntity | null {
    if (index < 0) return null;
    return w.guns[index] ?? null;
  }

  /**
   * The gun this player should be tracking. Guns re-tint to the holder's team on
   * pickup, so `g.team === p.team` already means "ours", stolen or not. Prefer a
   * held one — where the gun *is* matters more than where it belongs.
   */
  private teamGun(w: WorldState, p: PlayerState): GunEntity | null {
    let ours: GunEntity | null = null;
    let oursHeld: GunEntity | null = null;
    let teammateHeld: GunEntity | null = null;
    for (const g of w.guns) {
      if (g.team === p.team) {
        if (!ours) ours = g;
        if (g.holder && !oursHeld) oursHeld = g;
      } else if (g.holder && !teammateHeld && w.players[g.holder]?.team === p.team) {
        teammateHeld = g;
      }
    }
    return oursHeld ?? ours ?? teammateHeld;
  }

  private buildPips(root: HTMLElement): void {
    for (let i = 0; i < ROUNDS_TO_WIN; i++) {
      const pip = document.createElement('i');
      pip.className = 'ot-scorepip';
      root.appendChild(pip);
    }
  }

  private bumpPips(root: HTMLElement, score: number): void {
    const pips = root.children;
    for (let i = 0; i < pips.length; i++) {
      (pips[i] as HTMLElement).classList.toggle('is-on', i < score);
    }
  }
}

function formatClock(ms: number): string {
  const total = Math.max(0, Math.ceil(ms / 1000));
  const m = Math.floor(total / 60);
  const s = total % 60;
  return `${m}:${s < 10 ? '0' : ''}${s}`;
}
